import { FieldValues, UseFormWatch } from 'react-hook-form';

// Logging helper - only log in development environments
export const IS_DEV = import.meta.env.VITE_TEMP_MEE_ENV === 'development';
export const log = {
  info: (message: string, ...args: any[]) => {
    if (IS_DEV) console.info(`[AutoSave] ${message}`, ...args);
  },
  error: (message: string, ...args: any[]) => {
    if (IS_DEV) console.error(`[AutoSave] ${message}`, ...args);
  },
};

export enum AutoSaveStatus {
  Idle = 'idle',
  Saving = 'saving',
  Saved = 'saved',
  Error = 'error',
  Retrying = 'retrying',
}

/**
 * Configuration options for the useAutoSave hook
 */
export interface IUseAutoSaveOptions<T extends FieldValues, F extends FieldValues = T> {
  /**
   * React Hook Form's watch function to monitor form changes
   */
  watch: UseFormWatch<F>;

  /**
   * Async function that saves form values to backend
   */
  saveFn: (values: T) => Promise<any>;

  /**
   * Whether auto-save is enabled
   */
  enabled?: boolean;

  /**
   * Milliseconds to wait before triggering save after last change
   * Prevents excessive saves during rapid typing
   */
  debounceMs?: number;

  /**
   * Maximum number of retry attempts on save failure
   * Note: Retries infinitely when offline until connection resumes
   */
  maxRetries?: number;

  /**
   * Initial form values to prevent unnecessary save on mount
   * When provided, won't trigger save if form is populated with these values
   */
  initialValues?: T;
}

/**
 * Return value from the useAutoSave hook
 */
export interface IUseAutoSaveResult {
  /**
   * Current save status
   * - 'idle': No save operation in progress
   * - 'saving': Currently saving to backend
   * - 'saved': Save completed successfully (briefly shown before reverting to idle)
   * - 'error': Save failed after all retry attempts
   * - 'retrying': Retrying failed save attempt
   */
  status: AutoSaveStatus;

  /**
   * Timestamp of last successful save
   * Null if no save has completed yet
   */
  lastSavedAt?: Date | null;

  /**
   * Error object from last failed save attempt
   * Null if no error or last save succeeded
   */
  error?: unknown;

  /**
   * Whether the browser is currently offline
   * Automatically detected via navigator.onLine
   */
  isOffline: boolean;
}
